/**
 * Состояние сцены у клиента и его изменение входящими сообщениями.
 *
 * Модуль намеренно чистый: на вход — прежнее состояние и то, что
 * пришло из сокета, на выход — новое состояние и решение, надо ли
 * просить пересинхронизацию. Ни DOM, ни сети.
 *
 * ОБЩЕЕ ДЛЯ ОВЕРЛЕЯ И ПАНЕЛИ. Оба — клиенты одного потока дельт,
 * и оба обязаны считать версии одинаково. Держать слежение
 * за версией только в оверлее значило бы завести панели вторую
 * такую же реализацию, а расхождение между ними выглядело бы как
 * «у модератора одно, в эфире другое» — то есть как поломка,
 * которую ищут где угодно, только не здесь.
 *
 * ГЛАВНОЕ ЗДЕСЬ — СЛЕЖЕНИЕ ЗА ВЕРСИЕЙ. Дельты применяются подряд,
 * и пропуск хотя бы одной означает, что оверлей рисует состояние,
 * которого на сервере нет. Это не падение и не ошибка на экране:
 * картинка просто становится другой, и заметят это зрители.
 * Поэтому разрыв в нумерации — повод не «попробовать дальше»,
 * а остановиться и попросить всё заново.
 */
import { decodeMessage } from './message.js';
import { fromJson } from '@bufbuild/protobuf';
import { PatchSchema, SceneSchema } from '@owm/protocol';
import { applyOps } from './apply.js';
export const INITIAL_SCENE = { scene: null, version: 0n, problem: '' };
/**
 * Применяет входящее сообщение.
 *
 * Всё непонятное игнорируется молча и возвращает ПРЕЖНЮЮ ссылку:
 * по ней вызывающий понимает, что перерисовывать нечего. Оверлей
 * не имеет права падать от неизвестного сообщения — он работает
 * без присмотра на машине стримера в эфире.
 */
export function applyMessage(state, raw) {
    const message = parse(raw);
    if (message === null)
        return keep(state);
    switch (message.t) {
        case 'snapshot':
            return applySnapshot(state, message);
        case 'patch':
            return applyPatch(state, message);
        default:
            return keep(state);
    }
}
function applySnapshot(state, message) {
    const version = toVersion(message.version);
    if (version === null || !isRecord(message.scene)) {
        return keep(state);
    }
    let scene;
    try {
        scene = fromJson(SceneSchema, message.scene);
    }
    catch (error) {
        // Снапшот, который не разбирается, — это расхождение версий
        // протокола между сервером и оверлеем. Молчать нельзя: на экране
        // окажется пустота, и стример будет искать причину в OBS
        return fail(state, `снапшот не разобран: ${describe(error)}`);
    }
    return {
        state: { scene, version, problem: '' },
        resync: false,
    };
}
function applyPatch(state, message) {
    if (!isRecord(message.patch))
        return keep(state);
    let patch;
    try {
        patch = fromJson(PatchSchema, message.patch);
    }
    catch (error) {
        return fail(state, `дельта не разобрана: ${describe(error)}`);
    }
    // Дельта до первого снапшота применять некуда. Не поломка:
    // так выглядит сообщение, обогнавшее ответ на рукопожатие
    if (state.scene === null) {
        return { state, resync: true };
    }
    // РАЗРЫВ ВЕРСИИ. Дельты применяются подряд; пропуск означает,
    // что мы не знаем промежуточного состояния и применение поверх
    // даст не то, что на сервере. Просим всё заново
    if (patch.version !== state.version + 1n) {
        return {
            state: { ...state, problem: '' },
            resync: true,
        };
    }
    let scene;
    try {
        scene = applyOps(state.scene, patch.ops);
    }
    catch {
        // Операция не применилась: клиент разошёлся с сервером сильнее,
        // чем на одну версию. Дальше рисовать нельзя — просим заново
        return {
            state: { ...state, problem: '' },
            resync: true,
        };
    }
    return {
        state: { scene, version: patch.version, problem: '' },
        resync: false,
    };
}
function keep(state) {
    return { state, resync: false };
}
function fail(state, problem) {
    if (state.problem === problem)
        return keep(state);
    return { state: { ...state, problem }, resync: false };
}
function parse(raw) {
    const parsed = decodeMessage(raw);
    if (parsed === null)
        return null;
    if (!isRecord(parsed) || typeof parsed.t !== 'string')
        return null;
    return parsed;
}
/**
 * Разбирает версию.
 *
 * Строкой, потому что uint64 в JSON так и передаётся: в JavaScript
 * числа больше 2⁵³ теряют точность. Число тоже принимается —
 * от старого сервера, — но строка основной случай.
 */
function toVersion(raw) {
    try {
        if (typeof raw === 'string')
            return BigInt(raw);
        if (typeof raw === 'number' && Number.isSafeInteger(raw))
            return BigInt(raw);
    }
    catch {
        return null;
    }
    return null;
}
function isRecord(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
function describe(error) {
    return error instanceof Error ? error.message : String(error);
}
//# sourceMappingURL=state.js.map